import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BackNavigable,
  BackNavigationService,
  CommandStore,
  MengenEingabeComponent,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS } from '@softline/application';
import { Store } from '@softline/core';
import {
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR } from '@softline/ui-core';
import { BehaviorSubject, map, Subscription, switchMap } from 'rxjs';
import {
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
  SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_EMBALLAGE } from '../../../kommissionierung.shared';
import { LlrEmballageStore } from '../../../store/emballage.store';
import { LlrKommissionierungStore } from '../../../store/kommissionierung.store';
import {
  Kommissionierung,
  KommissionierungBewegung } from '../../../types/kommissionierung';

import { RepeatDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-emballage-erfassung',
  imports: [CommonModule, MengenEingabeComponent, ReactiveFormsModule,
    RepeatDirective,
    TranslatePipe],
  templateUrl: './emballagen-erfassung.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./emballagen-erfassung.component.css'] })
export class EmballagenErfassungComponent implements OnInit, OnDestroy, BackNavigable {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly backNavigationService = inject(BackNavigationService);

  private readonly palettenId = toSignal(
    this.activatedRoute.queryParamMap.pipe(
      map((queryParamMap) => queryParamMap.get('paletteId') ?? null)
    )
  );

  private kommissionierung?: Kommissionierung;
  private kommissionierungSubscription?: Subscription;
  private canExecuteAbschliessenButton$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);

  readonly kommissionierung$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
    LlrKommissionierungStore.getters.data
  );

  readonly bewegung = toSignal(
    this.activatedRoute.paramMap.pipe(
      map((paramMap) => paramMap.get('id') ?? ''),
      switchMap((id) =>
        this.kommissionierung$.pipe(
          map((komm) => {
            const index = komm?.bewebskomms.findIndex((o) => o.id + '' === id);

            if (index < 0) return null;

            return komm?.bewebskomms[index];
          })
        )
      )
    )
  );

  readonly emballagen = toSignal(
    this.store.observe(
      SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_EMBALLAGE,
      LlrEmballageStore.getters.all
    )
  );

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_EMBALLAGE,
    LlrEmballageStore.getters.loading
  );

  @HostListener('window:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
  }


  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);

    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: EmballagenErfassungComponent,
      commands: [
        {
          name: '#LLR_KOMMISSIONIERUNG.AKTIONEN.LABEL_PALETTE_ABSCHLIESSEN',
          class: 'menu bottom-menu primary',
          canExecute: this.canExecuteAbschliessenButton$.asObservable(),
          execute: async () => await this.abschliessen() }] });

    this.kommissionierungSubscription = this.kommissionierung$.subscribe(
      (komm) => (this.kommissionierung = komm)
    );

    if (!this.kommissionierung) return;
  }

  ngOnDestroy(): void {
    this.backNavigationService.set(undefined);

    if (this.kommissionierungSubscription && !this.kommissionierungSubscription.closed)
      this.kommissionierungSubscription.unsubscribe();

    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      EmballagenErfassungComponent
    );
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(
      [
        `llr-bskomm/bewegung/${this.bewegung()?.id}/artikeldaten-erfassung/${this.bewegung()?.id}/mhd-charge-erfassung`],
      {
        queryParams: {
          paletteId: this.palettenId() ?? '' } }
    );
  }

  updateEmballageMengeLocal(menge: number, emballage: KommissionierungBewegung) {
    const emballagen = this.emballagen();

    if (emballagen) {
      const index = emballagen.findIndex((o) => o?.id === emballage.id);
      const emballageParam = emballagen[index];

      if (index >= 0) {
        emballageParam.kommissionierDaten.menge = menge;

        this.store.commit(
          SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_EMBALLAGE,
          LlrEmballageStore.mutations.addOrUpdate,
          emballageParam
        );
      }
    }
  }

  async abschliessen(): Promise<void> {
    if (!this.kommissionierung) return;

    try {
      const bewe = await this.emballagenSchreiben(this.kommissionierung);

      if (!bewe) return;

      const beweFuerAbschliessen = this.bewegung();

      if (!beweFuerAbschliessen) return;

      const beweAbschliessenResult = await this.beweAbschliessen(
        beweFuerAbschliessen.idkopfbskomm + '',
        beweFuerAbschliessen.id + '',
        beweFuerAbschliessen
      );

      if (!beweAbschliessenResult) return;

      const beweAfterAbschluss = beweAbschliessenResult.bewebskomms.find(
        (o) => o.id === beweFuerAbschliessen.id
      );

      if (!beweAfterAbschluss) return;

      if (beweAfterAbschluss.kommissionierDaten.menge !== 0) {
        await this.router.navigate([
          `llr-bskomm/bewegung/${bewe.id}/artikeldaten-erfassung`]);
        return;
      }

      if (
        beweAbschliessenResult.bewebskomms.some(
          (bewe) => !bewe.kommissionierDaten.abgeschlossen
        )
      ) {
        try {
          await this.store.dispatch(
            SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
            LlrKommissionierungStore.actions.artikelPositionAbbrechen,
            {
              idkopfbskomm: this.kommissionierung.id + '',
              idbewebskomm: bewe.id + '',
              bewe: bewe }
          );
          const beweAfterAbschluss = beweAbschliessenResult.bewebskomms.find(
            (o) => o.id === bewe.id
          );

          if (!beweAfterAbschluss) return;

          if (beweAfterAbschluss.kommissionierDaten.menge !== 0) {
            await this.router.navigate([
              `llr-bskomm/bewegung/${bewe.id}/artikeldaten-erfassung`]);
            return;
          }

          if (
            beweAbschliessenResult.bewebskomms.some(
              (bewe) =>
                bewe.id === beweFuerAbschliessen.id &&
                bewe.kommissionierDaten.abgeschlossen
            )
          ) {
            await this.store.dispatch(
              SOFTLINE_FEATURE_MESSAGE_BAR,
              MessageBarStore.actions.success,
              {
                title: '#LLR_KOMMISSIONIERUNG.DIALOG_GLOBAL.TITEL_ARTIKEL_ABGESCHLOSSEN',
                message:
                  '#LLR_KOMMISSIONIERUNG.DIALOG_GLOBAL.LABEL_ARTIKEL_ABGESCHLOSSEN' }
            );
          }
          await this.router.navigate(['llr-bskomm/artikelauswahl']);
          return;
        } catch (e) {
          showRequestErrors(this.store, e);
        }
      }
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        {
          title:
            '#LLR_KOMMISSIONIERUNG.WARENZUGANG_BUCHEN.TITEL_ALLE_ARTIKEL_ABGESCHLOSSEN',
          message:
            '#LLR_KOMMISSIONIERUNG.WARENZUGANG_BUCHEN.LABEL_ALLE_ARTIKEL_ABGESCHLOSSEN' }
      );
      await this.router.navigate(['/llr-bskomm/warenzugang-abschliessen']);
    } catch (e) {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.closeAll
      );
      showRequestErrors(this.store, e);
    }
  }

  async emballagenSchreiben(
    kommissionierung: Kommissionierung
  ): Promise<KommissionierungBewegung | null> {
    try {
      const emballagen = this.emballagen() ?? [];

      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG_EMBALLAGE,
        LlrEmballageStore.actions.emballagenSchreiben,
        {
          idkopfbskomm: kommissionierung.id + '',
          emballagen: emballagen }
      );

      const beweOld = this.bewegung();
      const index = result.bewebskomms.findIndex((o) => o.id === beweOld?.id);
      return index > -1 ? result.bewebskomms[index] : null;
    } catch (e) {
      showRequestErrors(this.store, e);
      return null;
    }
  }

  async beweAbschliessen(
    idkopfbskomm: string,
    idbewebskomm: string,
    bewe: KommissionierungBewegung
  ): Promise<Kommissionierung | undefined> {
    try {
      if (bewe.kommissionierDaten.menge === 0) return;

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.loading
      );
      this.canExecuteAbschliessenButton$.next(false);

      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_LLR_KOMMISSIONIERUNG,
        LlrKommissionierungStore.actions.artikelpositionAbschliessen,
        {
          idkopfbskomm: idkopfbskomm,
          idbewebskomm: idbewebskomm,
          bewe: bewe }
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.closeAll
      );
      this.canExecuteAbschliessenButton$.next(true);

      return result;
    } catch (e) {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.closeAll
      );
      showRequestErrors(this.store, e);
      this.canExecuteAbschliessenButton$.next(true);
      return undefined;
    }
  }
}
